import {
  SelectionList,
  type SelectionListChangeEvent,
} from '@dixa/lib-shared-habla';
import type { Table as TanstackTable } from '@tanstack/react-table';
import type { SearchConversationEnriched } from 'lib-search-types';
import { useCallback } from 'react';

import type { DragMoveEvent } from 'utils/useOrderedDragDrop';

import type { ConversationsTableFeatures } from '../../../../conversationsTable.columns';
import type { UseConversationsTableLayoutResult } from '../../../../utils/useConversationsTableLayout';

import { ConversationsTablePreferencesColumnsItem } from './components/item/conversationsTablePreferencesColumnsItem';

export type ConversationsTablePreferencesColumnsProps = {
  /** The table whose columns the user chooses from. */
  table: TanstackTable<ConversationsTableFeatures, SearchConversationEnriched>;
  /** The user's saved layout of the table. */
  layout: UseConversationsTableLayoutResult;
};

/** A list that lets the user choose and reorder the columns of the conversations table. */
export const ConversationsTablePreferencesColumns = ({
  table,
  layout,
}: ConversationsTablePreferencesColumnsProps): JSX.Element => {
  const { setIsColumnVisible, setColumnOrder } = layout;

  const columns = table.getAllLeafColumns().flatMap((column) => {
    const label = column.columnDef.meta?.label;

    return label ? [{ column, label }] : [];
  });

  const visibleColumnIds = columns
    .filter(({ column }) => column.getIsVisible())
    .map(({ column }) => column.id);

  const handleChange = useCallback(
    ({ selectedItem }: SelectionListChangeEvent<string>) => {
      setIsColumnVisible(selectedItem.value, selectedItem.isSelected);
    },
    [setIsColumnVisible],
  );

  // Unlisted columns are left out of the order, so the table keeps them last.
  const handleMove = ({ dragIndex, hoverIndex }: DragMoveEvent) => {
    const columnIds = columns.map(({ column }) => column.id);
    const [movedColumnId] = columnIds.splice(dragIndex, 1);
    columnIds.splice(hoverIndex, 0, movedColumnId);
    setColumnOrder(columnIds);
  };

  return (
    <SelectionList
      isMultiple
      selectedValues={visibleColumnIds}
      onChange={handleChange}
    >
      <SelectionList.Section title="Columns">
        {columns.map(({ column, label }, index) => (
          <ConversationsTablePreferencesColumnsItem
            key={column.id}
            column={column}
            label={label}
            index={index}
            onMove={handleMove}
          />
        ))}
      </SelectionList.Section>
    </SelectionList>
  );
};
